/**
 * Hook for tracking online/offline status and pending sync count
 */

import { useState, useEffect, useCallback } from 'react'
import { offlineQueue, onSyncEvent, getPendingCount, getIsSyncing } from '../lib/offlineQueue'

interface OnlineStatus {
  isOnline: boolean
  pendingCount: number
  isSyncing: boolean
  lastSyncResult: { synced: number; failed: number } | null
  syncNow: () => Promise<void>
}

export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [pendingCount, setPendingCount] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncResult, setLastSyncResult] = useState<{ synced: number; failed: number } | null>(null)

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    const count = await getPendingCount()
    setPendingCount(count)
  }, [])

  // Manual sync trigger
  const syncNow = useCallback(async () => {
    if (!navigator.onLine) return
    await offlineQueue.syncAll()
  }, [])

  useEffect(() => {
    // Initial count
    updatePendingCount()

    // Online/offline events
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Sync events from queue
    const unsubscribe = onSyncEvent((event) => {
      switch (event.type) {
        case 'queue_updated':
          updatePendingCount()
          break
        case 'sync_started':
          setIsSyncing(true)
          break
        case 'sync_completed':
          setIsSyncing(false)
          if (event.data) {
            setLastSyncResult(event.data as { synced: number; failed: number })
          }
          updatePendingCount()
          break
        case 'online':
          setIsOnline(true)
          break
        case 'offline':
          setIsOnline(false)
          break
      }
    })

    // Check syncing state
    setIsSyncing(getIsSyncing())

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      unsubscribe()
    }
  }, [updatePendingCount])

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncResult,
    syncNow
  }
}

export default useOnlineStatus
